import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Add as AddIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

interface Flow {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'draft';
  createdAt: string;
  updatedAt: string;
  nodesCount: number;
}

const FlowList = () => {
  const navigate = useNavigate();
  const [flows, setFlows] = useState<Flow[]>([]);
  const storageKey = 'builders';

  // Load from localStorage on component mount
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
    setFlows(saved);
  }, []);

  const handleEdit = (flowId: string) => {
    navigate(`/flows/builder/${flowId}`);
  };

  const handleDuplicate = (flowId: string) => {
    const flowToDuplicate = flows.find(flow => flow.id === flowId);
    if (flowToDuplicate) {
      const newFlow: Flow = {
        ...flowToDuplicate,
        id: Date.now().toString(),
        name: `${flowToDuplicate.name} (Copy)`,
        status: 'draft',
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0],
      };
      setFlows(prev => [...prev, newFlow]);

      const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
      existing.push(newFlow);
      localStorage.setItem(storageKey, JSON.stringify(existing));
    }
  };

  const handleDelete = (flowId: string) => {
    setFlows(prev => prev.filter(flow => flow.id !== flowId));

    const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
    const updated = existing.filter((flow: Flow) => flow.id !== flowId);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  };

  const handleView = (flowId: string) => {
    navigate(`/flows/view/${flowId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'draft':
        return 'Draft';
      default:
        return status;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Builder Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/flows/new')}
        >
          Create New Builder
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Builder Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nodes</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Updated</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {flows.map(flow => (
                  <TableRow key={flow.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {flow.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {flow.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(flow.status)}
                        color={getStatusColor(flow.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {flow.nodesCount} nodes
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(flow.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(flow.updatedAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Flow">
                          <IconButton
                            size="small"
                            onClick={() => handleView(flow.id)}
                            sx={{ color: '#1976d2' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Flow">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(flow.id)}
                            sx={{ color: '#2e7d32' }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate Flow">
                          <IconButton
                            size="small"
                            onClick={() => handleDuplicate(flow.id)}
                            sx={{ color: '#ed6c02' }}
                          >
                            <DuplicateIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Flow">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(flow.id)}
                            sx={{ color: '#d32f2f' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FlowList;
